import { inject, Injectable } from '@angular/core';
import { destroyDetachedRouteHandle, type DetachedRouteHandle } from '@angular/router';

import { LogService } from '@core/log';

import { TAB_TTL_MS } from './nav.config';
import type { ScrollOffset } from './nav.types';

interface CacheEntry {
  readonly handle: DetachedRouteHandle;
  readonly expiresAt: number;
  readonly timeoutId: ReturnType<typeof setTimeout>;
}

@Injectable({ providedIn: 'root' })
export class NavCacheService {
  private readonly log = inject(LogService);
  private readonly cache = new Map<string, CacheEntry>();
  // Survives release(). The outlet takes the handle before the view is laid out.
  private readonly scroll = new Map<string, readonly ScrollOffset[]>();

  has(tabId: string): boolean {
    const entry = this.cache.get(tabId);

    if (!entry) return false;

    if (Date.now() >= entry.expiresAt) {
      this.delete(tabId);

      return false;
    }

    return true;
  }

  peek(tabId: string): DetachedRouteHandle | null {
    return this.cache.get(tabId)?.handle ?? null;
  }

  set(tabId: string, handle: DetachedRouteHandle, scroll: readonly ScrollOffset[] = []): void {
    const current = this.cache.get(tabId);

    if (current?.handle === handle) {
      this.release(tabId);
    } else {
      this.delete(tabId);
    }

    const expiresAt = Date.now() + TAB_TTL_MS;
    const timeoutId = setTimeout(() => {
      const entry = this.cache.get(tabId);

      if (entry?.handle === handle) this.delete(tabId);
    }, TAB_TTL_MS);

    this.cache.set(tabId, { handle, expiresAt, timeoutId });

    if (scroll.length) this.scroll.set(tabId, scroll);
    else this.scroll.delete(tabId);
  }

  peekScroll(tabId: string): readonly ScrollOffset[] {
    return this.scroll.get(tabId) ?? [];
  }

  clearScroll(tabId: string): void {
    this.scroll.delete(tabId);
  }

  release(tabId: string): void {
    const entry = this.cache.get(tabId);

    if (!entry) return;

    clearTimeout(entry.timeoutId);
    this.cache.delete(tabId);
  }

  delete(tabId: string): void {
    const entry = this.cache.get(tabId);

    if (!entry) return;

    clearTimeout(entry.timeoutId);
    this.cache.delete(tabId);
    this.scroll.delete(tabId);

    try {
      destroyDetachedRouteHandle(entry.handle);
    } catch (err) {
      this.log.warn('[NavCacheService] Failed to destroy handle', err);
    }
  }

  clear(): void {
    for (const tabId of [...this.cache.keys()]) this.delete(tabId);

    this.scroll.clear();
  }
}
